import React, { useEffect, useMemo, useRef, useState } from "react";
import dayjs, { Dayjs } from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";

import CalendarWithDots from "./calendar-with-dots";
import CalendarBooking from "./calendar-booking";
import BookingDetails from "./booking-details";
import type { BookingItem, BookingStatus } from "./booking.types";

import {
  getBookingAppointmentCalendar,
  getBookingAppointmentDetails,
  getBookingAppointmentsByDate,
} from "../../../api/booking.api";

import "./booking-appointment.scss";

dayjs.extend(customParseFormat);

const toTitleStatus = (s?: string): BookingItem["status"] => {
  const v = (s ?? "").toLowerCase();
  if (v === "confirmed" || v === "paid") return "Confirmed";
  if (v === "cancelled" || v === "canceled") return "Cancelled";
  if (v === "completed") return "Completed";
  return "Pending";
};

// parses: "February 13, 2026 at 11:30 AM"
const parseApiDateTime = (raw?: string) => {
  if (!raw) return null;

  const cleaned = raw.replace(/\s+at\s+/i, " ").trim();

  const parsed = dayjs(
    cleaned,
    ["MMMM D, YYYY h:mm A", "MMMM DD, YYYY h:mm A", "MMMM D, YYYY hh:mm A", "MMMM DD, YYYY hh:mm A"],
    true
  );
  if (parsed.isValid()) return parsed;

  const loose = dayjs(cleaned);
  return loose.isValid() ? loose : null;
};

export default function BookingAppointmentCalendar() {
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const [selectedDate, setSelectedDate] = useState<Dayjs>(dayjs());
  const [activeOrderId, setActiveOrderId] = useState<string | null>(null);

  // month dots data from API (set of YYYY-MM-DD)
  const [bookingDays, setBookingDays] = useState<Set<string>>(new Set());
  const currentMonthRef = useRef<string>("");
  // list data from by-date API
  const [bookings, setBookings] = useState<BookingItem[]>([]);
  const [loadingList, setLoadingList] = useState(false);

  // details data from details API
  const [details, setDetails] = useState<any>(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  const selectedDateBookings = useMemo(() => {
    const key = selectedDate.format("YYYY-MM-DD");
    return bookings.filter((b) => dayjs(b.appointmentDate).format("YYYY-MM-DD") === key);
  }, [bookings, selectedDate]);

  const activeBooking = useMemo(() => {
    if (!activeOrderId) return null;
    return bookings.find((b) => String(b.id) === String(activeOrderId)) ?? null;
  }, [bookings, activeOrderId]);

  // ---------------------------
  // 1) Calendar dots (month)
  // ---------------------------
  const fetchCalendarMonth = async (monthDayjs: Dayjs) => {
      const month = monthDayjs.format("YYYY-MM");
      currentMonthRef.current = month;
    try {
      const res = await getBookingAppointmentCalendar({ timezone, month });
      // Discard stale responses if the user navigated to another month
      if (currentMonthRef.current !== month) return;
      const datesArr = res?.data?.data?.dates ?? [];
      const set = new Set<string>();

      datesArr.forEach((x: any) => {
        if (x?.date) set.add(dayjs(x.date).format("YYYY-MM-DD"));
      });

      setBookingDays(set);
    } catch {
      if (currentMonthRef.current === month) setBookingDays(new Set()); //  no mock fallback
    }
  };

  // ---------------------------
  // 2) By-date list
  // ---------------------------
  const fetchByDate = async (d: Dayjs) => {
    setLoadingList(true);
    try {
      const date = d.format("YYYY-MM-DD");
      const res = await getBookingAppointmentsByDate({ timezone, date });

      const apiBookings = res?.data?.data?.bookings ?? [];

      const mapped: BookingItem[] = apiBookings
        .map((b: any) => {
          //  orderId is the numeric id required by details API
          const orderId =
            b.orderId ?? b.id ?? b.bookingAppointmentId ?? b.booking_id ?? null;

          if (orderId == null) return null;

          const dt =
            parseApiDateTime(b.appointmentDate) ||
            parseApiDateTime(b.appointmentTime) ||
            null;

          return {
            //  store numeric orderId in id (so details uses it)
            id: String(orderId),

            // display code (optional)
            bookingId: b.bookingId,

            customerName: b.customerName,
            serviceType: b.serviceType,
            amountPaid: Number(b.amountPaid || 0),

            //  normalize to "YYYY-MM-DD" so your existing date filter works
            appointmentDate: dt ? dt.format("YYYY-MM-DD") : date,

            //  normalize to time-only string
            appointmentTime: dt ? dt.format("h:mm A") : "",

            dateOfRequest: b.dateRequested,
            status: toTitleStatus(b.bookingStatus),
          } as BookingItem;
        })
        .filter(Boolean) as BookingItem[];

      setBookings(mapped);
    } catch {
      setBookings([]); //  no mock fallback
    } finally {
      setLoadingList(false);
    }
  };

  // initial load
  useEffect(() => {
    fetchCalendarMonth(selectedDate);
    fetchByDate(selectedDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // fetch list when date changes
  useEffect(() => {
    fetchByDate(selectedDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate]);

  // ---------------------------
  // 3) Details
  // ---------------------------
  const handleViewDetails = async (orderId: string) => {
    //  guard: avoid /details/undefined
    if (!orderId || orderId === "undefined" || orderId === "null") {
      console.warn("Missing orderId. Cannot call details API.");
      return;
    }

    setActiveOrderId(orderId);
    setLoadingDetails(true);
    setDetails(null);

    try {
      const res = await getBookingAppointmentDetails({
        timezone,
        bookingId: orderId, //  API extracts numericId if needed
      });
      setDetails(res?.data?.data ?? null);
    } catch {
      setDetails(null);
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleBackToList = () => {
    setActiveOrderId(null);
    setDetails(null);
  };

  const handleStatusChange = (id: string, status: BookingStatus) => {
    console.log("change status", id, status);
  };

  return (
    <div className="mg-bookingPage">
      <div className="mg-bookingPage__left">
        <CalendarWithDots
          selectedDate={selectedDate}
          onChange={(d) => {
            setSelectedDate(d);
            setActiveOrderId(null);
            setDetails(null);
          }}
          bookingDays={bookingDays}
          onMonthChange={(m) => fetchCalendarMonth(m)}
        />
      </div>

      <div className="mg-bookingPage__right">
        {!activeOrderId ? (
          <>
            <div className="mg-rightHeader">
              <div className="mg-rightHeader__title">
                Contract Orders
              </div>
              <div className="mg-rightHeader__date">
                {selectedDate.format("MMM D, YYYY")}
              </div>
            </div>

            {loadingList ? (
              <div className="mg-emptyState">Loading orders...</div>
            ) : selectedDateBookings.length === 0 ? (
              <div className="mg-emptyState">No Orders for the selected date, go to list view to see all your Orders</div>
            ) : (
              <div className="mg-bookingList">
                {selectedDateBookings.map((b) => (
                  <CalendarBooking
                    key={b.bookingId || b.id}
                    booking={b}
                    onViewDetails={() => {
                      //  IMPORTANT: call details using orderId stored in b.id
                      handleViewDetails(String(b.id));
                    }}
                  />
                ))}
              </div>
            )}
          </>
        ) : (
          <BookingDetails
            booking={activeBooking}
            apiDetails={details}
            loading={loadingDetails}
            onBack={handleBackToList}
            onStatusChange={handleStatusChange}
            orderId={activeOrderId}
            fetchDetails={handleViewDetails}
          />
        )}
      </div>
    </div>
  );
}
